import { Injector } from '@angular/core';
import { GridColumnActionType } from '../../../../shared/components/grid/grid-config';
import { UserDetail } from '../../../admin/admin.model';
import { MatDialog } from '@angular/material/dialog';
import {
  EditUserRolesComponent,
  EditUserRolesDialogData,
} from '../../../admin/components/edit-user-roles/edit-user-roles.component';
import { UserStore } from 'app/features/user/store/user.store';
import { NotificationService } from 'app/shared/services/notification.service';
import { ModalComponentInterface, ModalService } from 'app/shared/components/modal';
import { CustomersService } from '../../services/customers.service';
import { OverlayService } from 'app/shared/services/overlay.service';

enum PortalAccountActionNames {
  'editRoles' = 'editRoles',
  'resetPassword' = 'resetPassword',
  'deleteUser' = 'deleteUser',
}

// Factory function to create portal account actions
export function createPortalAccountsActions(): GridColumnActionType<UserDetail>[] {
  const portalAccountActionHandlers: { [key in PortalAccountActionNames]: GridColumnActionType<UserDetail> } = {
    [PortalAccountActionNames.editRoles]: {
      label: 'Edit Roles',
      canShow: (user: UserDetail, component: Object, injector: Injector) => {
        // Always show for users with UPDATE_CUSTOMER permission (checked at tab level)
        return true;
      },
      handler: (user: UserDetail, component: any, injector: Injector) => {
        const dialog = injector.get(MatDialog);
        const userStore = injector.get(UserStore);
        const notificationService = injector.get(NotificationService);
        const customersService = injector.get(CustomersService);

        const activeProfile = userStore.activeProfile();
        const currentUserId = userStore.userInfo()?.userInfo?.userId;

        if (!activeProfile) {
          notificationService.error('No active profile selected');
          return;
        }

        // Get customerId from parent component (grid passes itself, not the parent)
        const customerId = component.parent?.customerId;
        if (!customerId) {
          notificationService.error('Customer ID not found');
          return;
        }

        const dialogData: EditUserRolesDialogData = {
          user: user,
          profileId: activeProfile.profileDetail.profileId,
          profileName: activeProfile.profileDetail.profileName,
          currentUserId: currentUserId || '',
          isEditingSelf: false, // Portal users can't edit themselves from this interface
          customerId: customerId, // Add customerId for portal user role editing
        };

        const dialogRef = dialog.open(EditUserRolesComponent, {
          width: '840px',
          data: dialogData,
          disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result === true) {
            // Reload portal users in the component
            if (component.loadPortalUsers) {
              component.loadPortalUsers();
            }
          }
        });
      },
    },
    [PortalAccountActionNames.resetPassword]: {
      label: 'Reset Password',
      canShow: (user: UserDetail, component: Object, injector: Injector) => {
        // Always show for users with UPDATE_CUSTOMER permission (checked at tab level)
        return true;
      },
      handler: (user: UserDetail, component: any, injector: Injector) => {
        const modalService = injector.get(ModalService);
        const notificationService = injector.get(NotificationService);
        const customersService = injector.get(CustomersService);
        const overlayService = injector.get(OverlayService);

        // Get customerId from parent component (grid passes itself, not the parent)
        const customerId = component.parent?.customerId;
        if (!customerId) {
          notificationService.error('Customer ID not found');
          return;
        }

        // Open reset password modal
        import('../reset-portal-user-password/reset-portal-user-password.component').then((m) => {
          modalService.openModal({
            title: `Reset Password for ${user.firstName} ${user.lastName}`,
            body: m.ResetPortalUserPasswordComponent,
            data: {},
            confirm: {
              label: 'Reset Password',
              action: (modal: ModalComponentInterface, resetPasswordComponent: any) => {
                const form = resetPasswordComponent.form;
                if (!form.valid) {
                  form.markAllAsTouched();
                  return;
                }

                const newPassword = form.controls.newPassword.value;
                const forcePasswordChange = form.controls.forcePasswordChange.value !== false;

                overlayService.showOverlay();
                customersService
                  .resetCustomerPortalUserPassword(customerId, user.userNumber, newPassword, forcePasswordChange)
                  .subscribe({
                    next: () => {
                      overlayService.hideOverlay();
                      const message = forcePasswordChange
                        ? 'Password reset successfully. User will be prompted to change password on next login. A confirmation email has been sent to the user.'
                        : 'Password reset successfully. A confirmation email has been sent to the user.';
                      notificationService.success(message);
                      modal.close();
                    },
                    error: (error) => {
                      overlayService.hideOverlay();
                      notificationService.error(`Failed to reset password: ${error.message}`);
                    },
                  });
              },
            },
            dismiss: {
              label: 'Cancel',
              action: (modal: ModalComponentInterface) => {
                modal.close();
              },
            },
          });
        });
      },
    },
    [PortalAccountActionNames.deleteUser]: {
      label: 'Delete User',
      canShow: (user: UserDetail, component: Object, injector: Injector) => {
        // Always show for users with UPDATE_CUSTOMER permission (checked at tab level)
        return true;
      },
      handler: (user: UserDetail, component: any, injector: Injector) => {
        const modalService = injector.get(ModalService);
        const customersService = injector.get(CustomersService);
        const notificationService = injector.get(NotificationService);
        const overlayService = injector.get(OverlayService);

        // Get customerId from parent component (grid passes itself, not the parent)
        const customerId = component.parent?.customerId;
        if (!customerId) {
          notificationService.error('Customer ID not found');
          return;
        }

        modalService.openModal({
          title: 'Delete Portal User',
          body: `Are you sure you want to delete portal user '${user.firstName} ${user.lastName} (${user.userId})'? This action cannot be undone.`,
          dismiss: {
            label: 'Cancel',
            action: (modal: ModalComponentInterface) => {
              modal.close();
            },
          },
          confirm: {
            label: 'Delete',
            action: (modal: ModalComponentInterface) => {
              overlayService.showOverlay();
              customersService.deleteCustomerPortalUser(customerId, user.userNumber).subscribe({
                next: () => {
                  overlayService.hideOverlay();
                  notificationService.success(`Portal user ${user.userId} deleted successfully`);

                  // Reload portal users in the component
                  if (component.loadPortalUsers) {
                    component.loadPortalUsers();
                  }

                  modal.close();
                },
                error: (error) => {
                  overlayService.hideOverlay();
                  notificationService.error(`Failed to delete portal user: ${error.message}`);
                },
              });
            },
          },
        });
      },
    },
  };

  return [
    portalAccountActionHandlers[PortalAccountActionNames.editRoles],
    portalAccountActionHandlers[PortalAccountActionNames.resetPassword],
    portalAccountActionHandlers[PortalAccountActionNames.deleteUser],
  ];
}
